import React, { useState } from 'react';
import styles from '../styles/FaqPage.module.css';
import step1 from '../shared/step1.png';
import step2 from '../shared/step2.png';
import step3 from '../shared/step3.png';

const tabs = [
  { id: 'faq', label: 'FAQ Questions' },
  { id: 'image', label: 'FTS Explain Image' },
  { id: 'video', label: 'FTS Explain Video' },
];

// ================== FAQ Questions (20) ==================
const faqs = [
  {
    q: 'What is Full Trucking Service (FTS)?',
    a: 'FTS is an all-in-one trucking management platform. It integrates operations, logistics, finance, communication, and AI analysis into one system, helping companies, dispatchers, and drivers run daily activities efficiently.'
  },
  {
    q: 'How do I set up the company database?',
    a: 'Go to Operations. Add drivers first, then trucks and trailers, followed by shippers, consignees, brokers, and employees. This creates the foundation for daily dispatch and finance activities.'
  },
  {
    q: 'How do I create a load?',
    a: 'From Load Entry, add shipper, consignee, pickup/delivery, and rates. Assign a driver, truck, and trailer. Once saved, it appears in the Scheduler and Tracker for live monitoring.'
  },
  {
    q: 'How do clients track shipments?',
    a: 'Each load generates a tracking link. Share it with clients, who can follow real-time updates without logging in.'
  },
  {
    q: 'How does the Driver App integrate?',
    a: 'Drivers receive load details, upload scanned documents, and send PODs directly. Everything syncs instantly in FTS.'
  },
  {
    q: 'How do I pay drivers?',
    a: 'Go to Finance > Driver Pay. Select loads, calculate pay per mile/load, and generate payment slips.'
  },
  {
    q: 'How are invoices managed?',
    a: 'Finance > Invoices. Select completed loads, generate invoice, and send directly to brokers or factoring companies.'
  },
  {
    q: 'Can I run IFTA and fuel reports?',
    a: 'Yes. Finance includes IFTA, fuel consumption, and expense tracking for compliance and financial planning.'
  },
  {
    q: 'What analytics are available?',
    a: 'Dashboard provides KPIs, dispatcher performance, cost vs revenue, driver profitability, and AI-driven suggestions.'
  },
  {
    q: 'Can I customize employee access?',
    a: 'Yes. Employees can be assigned roles: dispatcher, accountant, manager, with restricted or full access rights.'
  },
  {
    q: 'Is FTS cloud-based?',
    a: 'Yes. All data is stored securely in the cloud, available from desktop and mobile devices anywhere.'
  },
  {
    q: 'Can multiple companies use FTS?',
    a: 'Yes. Each company account has its own isolated database with separate drivers, trucks, and finances.'
  },
  {
    q: 'How do I reset a driver account?',
    a: 'Go to Operations > Drivers. Select driver, reset password, and share login for the Driver App.'
  },
  {
    q: 'Can I attach documents to loads?',
    a: 'Yes. Upload BOL, rate confirmations, and proof of delivery. Documents remain archived per load.'
  },
  {
    q: 'How do I see dispatcher KPIs?',
    a: 'Dashboard tracks dispatcher performance: loads managed, on-time deliveries, and profit margins.'
  },
  {
    q: 'Can I manage payroll for employees?',
    a: 'Yes. Finance > Payroll lets you process salaries for dispatchers, accountants, or other roles.'
  },
  {
    q: 'What happens after a driver uploads a document?',
    a: 'It is attached to the active load automatically and visible instantly to dispatch and finance.'
  },
  {
    q: 'How do I monitor truck expenses?',
    a: 'Finance > Costs. Enter maintenance, fuel, and insurance. Dashboard calculates per-truck profitability.'
  },
  {
    q: 'Does FTS support AI features?',
    a: 'Yes. AI suggests cost optimizations, highlights delays, and provides predictive analytics on loads.'
  },
  {
    q: 'How do I check company performance?',
    a: 'Dashboard shows open/closed loads, income vs expenses, and driver/truck-level analysis at a glance.'
  }
];

// ================== Explain Image ==================
const imageSteps = [
  {
    step: 'Step 1: Operations – Building the Base',
    points: [
  {
  title: 'Managing Drivers',
  text: 'How to add and manage a Driver: first go to the Header Menu, then to Operations, and select Drivers.',
  images: [
    { src: step1, desc: 'Here you add the Driver details as you prefer.' },
    { src: step2, desc: 'After you press Add Driver, the Driver will appear in the Existing Driver section at the bottom of the page. There you can edit it, delete it, or mark it as Not More Working Driver.' },
    { src: step3, desc: 'After you add a Load, go to Fleet and add a Truck so later you can assign that truck to the specific Driver.' }
  ]
},
      { title: 'Managing Fleet', text: 'Add trucks with VIN, plate, and assign to drivers for dispatching.', img: '/images/fts/fleet.png' },
      { title: 'Managing Trailers', text: 'Register trailers and link them to trucks or loads.', img: '/images/fts/trailers.png' },
      { title: 'Managing Shippers & Brokers', text: 'Add shippers, consignees, and brokers for load creation and billing.', img: '/images/fts/shipper.png' },
      { title: 'Managing Employees', text: 'Add dispatchers and accountants with role-based access.', img: '/images/fts/employees.png' }
    ]
  },
  {
    step: 'Step 2: Load Entry & Logistics',
    points: [
      { title: 'Add Load', text: 'Create a new load with shipper, consignee, dates, and rates.', img: '/images/fts/load-entry.png' },
      { title: 'Manage Loads', text: 'Edit and update load details as operations progress.', img: '/images/fts/manage-load.png' },
      { title: 'Track Status', text: 'Use Load Tracker to monitor progress in real time.', img: '/images/fts/status.png' }
    ]
  }
];

// ================== Explain Video ==================
const videoSteps = [
  { 
    step: 'Step 1: Operations – Building the Base', 
    videoUrl: 'https://www.youtube.com/embed/C1wSqIy0zSo' 
  },

  { step: 'Step 2: Load Entry & Logistics', videoUrl: 'https://www.youtube.com/embed/yyyy' },
  { step: 'Step 3: Trucking & Communication', videoUrl: 'https://www.youtube.com/embed/zzzz' },
  { step: 'Step 4: Finance – From Loads to Money Flow', videoUrl: 'https://www.youtube.com/embed/aaaa' },
  { step: 'Step 5: Dashboard – Insights & AI Tools', videoUrl: 'https://www.youtube.com/embed/bbbb' }
];

export default function FaqPage() {
  const [active, setActive] = useState('faq');
  const [openImage, setOpenImage] = useState(null);
  const [openVideo, setOpenVideo] = useState(null);

  return (
    <div className={styles.FAQPage}>
      <header className={styles.FAQHero}>
        <h1 className={styles.FAQTitle}>Welcome to the FTS Help Center</h1>
        <p className={styles.FAQSubtitle}>Clear answers. Operational guides. End-to-end training.</p>
        <div className={styles.FAQActions}>
          {tabs.map(t => (
            <button
              key={t.id}
              className={`${styles.FAQActionBtn} ${active === t.id ? styles.FAQActionBtnActive : ''}`}
              onClick={() => setActive(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>
      </header>

      <main className={styles.FAQMain}>
        {/* FAQ */}
        {active === 'faq' && (
          <section>
            <h2 className={styles.FAQBlockTitle}>FAQ Questions</h2>
            {faqs.map((item, idx) => (
              <details key={idx} className={styles.FAQItem}>
                <summary className={styles.FAQQ}>{item.q}</summary>
                <div className={styles.FAQA}>{item.a}</div>
              </details>
            ))}
          </section>
        )}

        {/* Explain Image */}
        {active === 'image' && (
          <section>
            <h2 className={styles.FAQBlockTitle}>FTS Explain Image</h2>
            <div className={styles.FAQExplainGrid}>
              {imageSteps.map((step, idx) => (
                <div key={idx} className={styles.FAQExplainCard}>
                  <h3 className={styles.FAQExplainTitle}>{step.step}</h3>
                  <ul className={styles.FAQExplainList}>
                    {step.points.map((p, i) => (
                      <li key={i}>
                        <button
                          className={styles.FAQSubBtn}
                          onClick={() => setOpenImage({ step: idx, point: i })}
                        >
                          {p.title}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Explain Video */}
        {active === 'video' && (
          <section>
            <h2 className={styles.FAQBlockTitle}>FTS Explain Video</h2>
            <div className={styles.FAQExplainGrid}>
              {videoSteps.map((step, idx) => (
                <div key={idx} className={styles.FAQExplainCard}>
                  <button
                    className={styles.FAQVideoBtn}
                    onClick={() => setOpenVideo(idx)}
                  >
                    {step.step}
                  </button>
                </div>
              ))}
            </div>
          </section>
        )}
      </main>

    {/* MODAL for Explain Image */}
{openImage && (
  <div className={styles.FAQModal}>
    <div className={styles.FAQModalContent}>
      <button className={styles.FAQModalClose} onClick={() => setOpenImage(null)}>×</button>
      <h3>{imageSteps[openImage.step].points[openImage.point].title}</h3>
      <p>{imageSteps[openImage.step].points[openImage.point].text}</p>

      {/* If this point has multiple images */}
      {imageSteps[openImage.step].points[openImage.point].images ? (
        imageSteps[openImage.step].points[openImage.point].images.map((img, idx) => (
          <div key={idx} className={styles.FAQImageBlock}>
            <img src={img.src} alt={`Step ${idx + 1}`} className={styles.FAQExplainImg} />
            {img.desc && <p className={styles.FAQImgDesc}>{img.desc}</p>}
          </div>
        ))
      ) : (
        <img
          src={imageSteps[openImage.step].points[openImage.point].img}
          alt={imageSteps[openImage.step].points[openImage.point].title}
          className={styles.FAQExplainImg}
        />
      )}
    </div>
  </div>
)}


      {/* MODAL pentru Explain Video */}
      {openVideo !== null && (
        <div className={styles.FAQModal}>
          <div className={styles.FAQModalContent}>
            <button className={styles.FAQModalClose} onClick={() => setOpenVideo(null)}>×</button>
            <iframe
              src={videoSteps[openVideo].videoUrl}
              title={videoSteps[openVideo].step}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      )}
    </div>
  );
}
